/**
 * Home-carousel deep-links on Products / Content pages.
 * Renders the same catalog cards + masonry grid as platform/category browse
 * (not the StayUpToDate individual-post feed chrome).
 */
import React, { useMemo, useState, useCallback } from "react";
import { useQuery } from "../../../Utils/query";
import { useLoadRenderComponent } from "../../../components/StayUpToDateComponent/StayUpToDateRenderComponent/useLoadRenderComponent";
import { WEBSITE_TEMPLATE_SURFACE } from "../../../Utils/StayUpToDate/componentSurface";
// Collection deep-link reads on public Products/Content — session-free client.
import { supabasePublic as supabase } from "../../../config/supabaseClient";
import type { CarouselData, ThemeTokens } from "./types";
import {
  mapFeedPostToShopCatalogContent,
  type ShopCatalogContent,
} from "./templateShopCatalog";
import { fetchTemplatePlatformPostsByIds } from "./templatePlatformPostsQuery";
import { useShopLinkIndividualPosts } from "./contentLooks";
import { ShopContentCatalogPanel } from "./templatePages/ShopContentCatalogPanel";
import { ShopProductCatalogPanel } from "./templatePages/ShopProductCatalogPanel";
import { TemplateStayUpToDateTabPanel } from "./TemplateStayUpToDateTabPanel";
import {
  carouselReadyForHomeStrip,
  TemplateCollectionHomeStripCatalog,
} from "./TemplateCollectionHomeStripCatalog";
import { isRelativeDayWindowDisplay } from "../../../Utils/StayUpToDate/stayUpToDateComponentTabFilters";

function parseComponentIdList(raw: unknown): string[] {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v ?? "").trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v ?? "").trim()).filter(Boolean);
        }
      } catch {
        /* fall through */
      }
    }
    return trimmed
      .split(/[,|\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function memberIdsFromRow(row: Record<string, unknown> | null | undefined): string[] {
  if (!row) return [];
  const postIds = parseComponentIdList(row.post_ids);
  if (postIds.length) return postIds;
  return parseComponentIdList(row.custom_order);
}

function orderByMemberIds<T extends { postId: string }>(
  items: T[],
  memberIds: string[],
): T[] {
  const byId = new Map(items.map((c) => [c.postId, c]));
  const ordered: T[] = [];
  const seen = new Set<string>();
  for (const id of memberIds) {
    const hit = byId.get(id);
    if (!hit || seen.has(id)) continue;
    seen.add(id);
    ordered.push(hit);
  }
  for (const c of items) {
    if (seen.has(c.postId)) continue;
    seen.add(c.postId);
    ordered.push(c);
  }
  return ordered;
}

type Props = {
  userId: string;
  username: string;
  renderSlug: string;
  T: ThemeTokens;
  pageVariant: "shop" | "content";
  cols: number;
  aspect?: string;
  buttonLabel?: string;
  showButton?: boolean;
  showCategory?: boolean;
  showPrice?: boolean;
  contentTabLabel?: string;
  /** False while the parent page layer is CSS-hidden (pre-mounted). */
  enabled?: boolean;
  /**
   * Optional: when parent already has homepage `CarouselData`, mount the home
   * strip fast path instead. Omitting this keeps the legacy lookup/feed path.
   */
  carousel?: CarouselData | null;
};

/**
 * Router only — known CREATOR carousel → home strip cache; otherwise the
 * unchanged legacy panel below. Legacy hooks never run on the fast path.
 */
export function TemplateCollectionCatalogPanel({
  carousel = null,
  ...props
}: Props) {
  if (props.enabled !== false && carouselReadyForHomeStrip(carousel)) {
    return (
      <TemplateCollectionHomeStripCatalog
        {...props}
        carousel={carousel}
      />
    );
  }
  return <TemplateCollectionCatalogPanelLegacy {...props} />;
}

function TemplateCollectionCatalogPanelLegacy({
  userId,
  username,
  renderSlug,
  T,
  pageVariant,
  cols,
  aspect = "4/5",
  buttonLabel = "Shop",
  showButton = true,
  showCategory = true,
  showPrice = true,
  enabled = true,
}: Props) {
  const slugTrim = enabled ? String(renderSlug || "").trim() : "";
  const isShop = pageVariant === "shop";
  const isContent = pageVariant === "content";
  const loadRow = (isShop || isContent) && Boolean(slugTrim);

  // Website template row + LiB stayuptodate row in parallel. Prefer the side
  // that already has member post_ids; don't block paint waiting for both when
  // one side already has a usable row.
  const templateRowQuery = useLoadRenderComponent(
    userId,
    loadRow ? slugTrim : "",
    WEBSITE_TEMPLATE_SURFACE,
    supabase,
  );
  const libRowQuery = useLoadRenderComponent(
    userId,
    loadRow ? slugTrim : "",
    undefined,
    supabase,
    username,
  );
  const templateRow = (templateRowQuery.data || null) as
    | Record<string, unknown>
    | null;
  const libRow = (libRowQuery.data || null) as Record<string, unknown> | null;
  const templateMemberCount = memberIdsFromRow(templateRow).length;
  const libMemberCount = memberIdsFromRow(libRow).length;
  const preferLibRow =
    templateMemberCount === 0 && (libMemberCount > 0 || Boolean(libRow));
  const data = preferLibRow ? libRow : templateRow || libRow;
  const templateSettled =
    !templateRowQuery.isLoading && !templateRowQuery.isFetching;
  const libSettled = !libRowQuery.isLoading && !libRowQuery.isFetching;
  // Ready once either side has member ids, or both queries have settled.
  const rowReady =
    templateMemberCount > 0 ||
    libMemberCount > 0 ||
    (templateSettled && libSettled);

  const row = (data || null) as Record<string, unknown> | null;
  const rowTypeLc = String(row?.type || "")
    .trim()
    .toLowerCase();
  // Collections store member *component* ids — never treat as platform posts.
  const isLibCollection =
    rowTypeLc === "carousel-collection" ||
    rowTypeLc === "collection-carousel";
  const memberIds = useMemo(
    () => (isContent && !isLibCollection ? memberIdsFromRow(row) : []),
    [isContent, isLibCollection, row],
  );
  const hasMemberIds = memberIds.length > 0;
  const rowPending =
    (isContent || isLibCollection) && Boolean(slugTrim) && !rowReady;

  const MEMBER_PAGE_SIZE = 24;
  const [memberVisibleCount, setMemberVisibleCount] = useState(MEMBER_PAGE_SIZE);
  // Reset page window when the collection changes.
  React.useEffect(() => {
    setMemberVisibleCount(MEMBER_PAGE_SIZE);
  }, [slugTrim, memberIds.join(",")]);
  const visibleMemberIds = useMemo(
    () => memberIds.slice(0, memberVisibleCount),
    [memberIds, memberVisibleCount],
  );
  const memberHasMore = memberIds.length > memberVisibleCount;
  const loadMoreMembers = useCallback(() => {
    setMemberVisibleCount((n) => n + MEMBER_PAGE_SIZE);
  }, []);

  // LiB day-windows like "Shop This Past Week" are shoppable *content* posts.
  // Opening them on Products still needs the content feed (not product-bank only).
  const shoppableFilterLc = String(row?.shoppable_content_filter || "")
    .trim()
    .toLowerCase();
  const isShoppableContentDayWindow =
    isShop &&
    !isLibCollection &&
    rowReady &&
    isRelativeDayWindowDisplay(
      String(row?.display_name || ""),
      String(row?.type || ""),
    ) &&
    shoppableFilterLc !== "products";

  // Products on shop collections; query/day-window posts on content collections
  // that have no post_ids. Looks callers never pass includeContent.
  // Lib Collections always expand via the shop member-component path.
  const shopFeed = useShopLinkIndividualPosts({
    shopUrl: slugTrim ? `/${slugTrim}` : "",
    username,
    userId,
    enabled:
      enabled &&
      (isShop || isLibCollection || (isContent && !hasMemberIds)) &&
      Boolean(slugTrim),
    includeContent:
      (isContent && !hasMemberIds && !isLibCollection) ||
      isShoppableContentDayWindow,
  });

  const contentQuery = useQuery(
    [
      "tpl-collection-content",
      userId,
      username,
      slugTrim,
      visibleMemberIds.join(","),
    ],
    async () => {
      const posts = await fetchTemplatePlatformPostsByIds({
        userId,
        username,
        platformKey: "related-content",
        postIds: visibleMemberIds,
        maxPosts: visibleMemberIds.length,
      });
      const out: ShopCatalogContent[] = [];
      const seen = new Set<string>();
      for (const raw of posts) {
        if (!raw || typeof raw !== "object") continue;
        const item = mapFeedPostToShopCatalogContent(
          raw as Record<string, unknown>,
        );
        if (!item || seen.has(item.postId)) continue;
        seen.add(item.postId);
        out.push(item);
      }
      return orderByMemberIds(out, visibleMemberIds);
    },
    {
      enabled:
        enabled &&
        isContent &&
        Boolean(userId && username && visibleMemberIds.length > 0),
      staleTime: 30_000,
      keepPreviousData: true,
    },
  );

  const memberContentItems = contentQuery.data ?? [];
  const feedContentItems = shopFeed.content;
  const contentItems = hasMemberIds ? memberContentItems : feedContentItems;
  const contentCatalogLoading =
    rowPending ||
    (hasMemberIds &&
      (contentQuery.isLoading || contentQuery.isFetching) &&
      memberContentItems.length === 0) ||
    (!hasMemberIds && shopFeed.loading && feedContentItems.length === 0);

  if (isShop || isLibCollection) {
    // Day-window shoppable content (LiB "Shop This Past Week") → same posts as
    // the LiB strip, as content cards (captions), not product-bank tiles.
    if (
      isShoppableContentDayWindow &&
      (shopFeed.content.length > 0 || shopFeed.loading || rowPending)
    ) {
      return (
        <ShopContentCatalogPanel
          catalogKey={`collection-shop-daywindow-${slugTrim}`}
          T={T}
          items={shopFeed.content}
          loading={shopFeed.loading || rowPending}
          userId={userId}
          username={username}
          cols={cols}
          aspect={aspect}
          buttonLabel={buttonLabel}
          hasMore={shopFeed.hasMore}
          loadingMore={shopFeed.loadingMore}
          onLoadMore={shopFeed.hasMore ? shopFeed.fetchMore : undefined}
        />
      );
    }
    // Zigzag masonry when the shop_link loader has rows or is still loading
    // (pending grid). StayUpToDate remains a last resort for types that loader
    // cannot map after it settles empty.
    if (
      shopFeed.products.length > 0 ||
      shopFeed.loading ||
      rowPending ||
      isLibCollection
    ) {
      return (
        <ShopProductCatalogPanel
          catalogKey={`collection-shop-${slugTrim}`}
          T={T}
          products={shopFeed.products}
          loading={shopFeed.loading || rowPending}
          userId={userId}
          cols={cols}
          aspect={aspect}
          showButton={showButton}
          showCategory={showCategory}
          showPrice={showPrice}
          buttonLabel={buttonLabel}
          shopView="all"
          hasMore={shopFeed.hasMore}
          loadingMore={shopFeed.loadingMore}
          onLoadMore={shopFeed.hasMore ? shopFeed.fetchMore : undefined}
        />
      );
    }
    return (
      <TemplateStayUpToDateTabPanel
        userId={userId}
        username={username}
        renderSlug={slugTrim}
        T={T}
        pageVariant="shop"
        gridColumns={cols}
        buttonLabel={buttonLabel}
        componentFrom={
          preferLibRow || !templateRow ? null : WEBSITE_TEMPLATE_SURFACE
        }
      />
    );
  }

  return (
    <ShopContentCatalogPanel
      catalogKey={`collection-content-${slugTrim}`}
      T={T}
      items={contentItems}
      loading={contentCatalogLoading}
      userId={userId}
      username={username}
      cols={cols}
      aspect={aspect}
      buttonLabel={buttonLabel}
      hasMore={
        hasMemberIds ? memberHasMore : shopFeed.hasMore
      }
      loadingMore={
        hasMemberIds
          ? contentQuery.isFetching && memberContentItems.length > 0
          : shopFeed.loadingMore
      }
      onLoadMore={
        hasMemberIds
          ? memberHasMore
            ? loadMoreMembers
            : undefined
          : shopFeed.hasMore
            ? shopFeed.fetchMore
            : undefined
      }
    />
  );
}
